// Calendar integration utilities for adding events to various calendar services

/**
 * Generate Google Calendar URL for an event
 */
export const generateGoogleCalendarUrl = (event) => {
  const { title, description, location, startDate, endDate } = event;
  
  // Format dates for Google Calendar (YYYYMMDDTHHMMSSZ)
  const formatDate = (date) => {
    return new Date(date).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${formatDate(startDate)}/${formatDate(endDate)}`,
    details: description || '',
    location: location || '',
  });
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Generate Outlook Calendar URL for an event
 */
export const generateOutlookCalendarUrl = (event) => {
  const { title, description, location, startDate, endDate } = event;
  
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: title,
    startdt: new Date(startDate).toISOString(),
    enddt: new Date(endDate).toISOString(),
    body: description || '',
    location: location || '',
  });
  
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

/**
 * Generate Yahoo Calendar URL for an event
 */
export const generateYahooCalendarUrl = (event) => {
  const { title, description, location, startDate, endDate } = event;
  
  // Format dates for Yahoo Calendar (YYYYMMDDTHHMMSS)
  const formatDate = (date) => {
    return new Date(date).toISOString().replace(/[-:]/g, '').split('.')[0];
  };
  
  // Calculate duration in hours and minutes
  const start = new Date(startDate);
  const end = new Date(endDate);
  const durationMs = end - start;
  const hours = Math.floor(durationMs / (1000 * 60 * 60)).toString().padStart(2, '0');
  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60)).toString().padStart(2, '0');
  
  const params = new URLSearchParams({
    v: '60',
    title: title,
    st: formatDate(startDate),
    dur: `${hours}${minutes}`,
    desc: description || '',
    in_loc: location || '',
  });
  
  return `https://calendar.yahoo.com/?${params.toString()}`;
};

/**
 * Generate ICS file content for download
 */
export const generateICSContent = (event) => {
  const { title, description, location, startDate, endDate, uid } = event;
  
  const formatDate = (date) => {
    return new Date(date).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };
  
  const escapeText = (text) => {
    if (!text) return '';
    return text.replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
  };
  
  return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//RazientSounds//Booking//EN
BEGIN:VEVENT
UID:${uid || `booking-${Date.now()}@razientsounds.com`}
DTSTAMP:${formatDate(new Date())}
DTSTART:${formatDate(startDate)}
DTEND:${formatDate(endDate)}
SUMMARY:${escapeText(title)}
DESCRIPTION:${escapeText(description)}
LOCATION:${escapeText(location)}
END:VEVENT
END:VCALENDAR`;
};

/**
 * Download ICS file
 */
export const downloadICSFile = (event) => {
  const content = generateICSContent(event);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
